"use client";

// Botón "Acceder con Google" según la guía de marca de Google Identity
// (https://developers.google.com/identity/branding-guidelines):
// - Tema claro: fondo #FFFFFF, borde interior 1 px #747775, texto #1F1F1F.
//   Tema oscuro: fondo #131314, borde interior 1 px #8E918F, texto #E3E3E3.
// - Texto: Google Sans Medium 14/20 en el botón de 40 px. Aquí el botón mide 50 px, como los demás controles
//   del login, y el texto usa 16 px, el mismo de los campos y del botón "Ingresar". Sin espaciado de letras
//   extra: la guía no lo pide.
//   Si Google Sans no está instalada, cae a Roboto/Arial; no se descarga ninguna fuente.
// - Márgenes web: 12 px antes del logo, 10 px entre logo y texto, 12 px después del texto. Escalados a 50 px
//   y redondeados a la grilla de 4 px (pasada unslop): 16 px a los lados, 12 px entre logo y texto, logo de 24 px.
// - Logo "G" a todo color, sin cambiar sus colores ni su proporción, siempre con el borde y el texto.
// - Texto localizado: "Acceder con Google" / "Sign in with Google" (nunca "Google" solo).
// La guía recomienda el botón del SDK de Google Identity Services, pero requiere un ID de cliente OAuth
// real (fase 2 del plan de backend). Hasta entonces se usa este botón propio que sigue las mismas reglas.

/** Logo "G" de Google a todo color. Reemplazar por el SVG del paquete oficial (signin-assets.zip) si se descarga. */
export function GoogleG({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true" focusable="false" className={className}>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

// Botón propio y no el `Button` de shadcn: sus variantes cambian el fondo y el radio en hover, y la guía
// de marca de Google solo permite una capa de estado encima de los colores oficiales.
export function GoogleSignInButton({ label, onClick, busy = false }: { label: string; onClick: () => void; busy?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-busy={busy}
      style={{ fontFamily: '"Google Sans", Roboto, Arial, sans-serif' }}
      className={[
        "relative w-full h-[50px] rounded-pill px-4",
        "flex items-center justify-center",
        // Borde interior de 1 px (inset) para no cambiar el tamaño del botón.
        "bg-white text-[#1F1F1F] shadow-[inset_0_0_0_1px_#747775]",
        "dark:bg-[#131314] dark:text-[#E3E3E3] dark:shadow-[inset_0_0_0_1px_#8E918F]",
        "text-[16px] leading-6 font-medium",
        // Estados de Material: capa del 8 % al pasar y 12 % al presionar, sin cambiar los colores de marca.
        "before:absolute before:inset-0 before:rounded-pill before:bg-[#1F1F1F] before:opacity-0 before:transition-opacity",
        "dark:before:bg-[#E3E3E3] hover:before:opacity-[0.08] active:before:opacity-[0.12]",
        "focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-primary/30",
        "transition-transform duration-200 ease-[cubic-bezier(0.32,0.72,0,1)] active:scale-[0.98]",
        "disabled:cursor-wait disabled:opacity-70",
      ].join(" ")}
    >
      {/* Logo y texto como una unidad centrada: 12 px entre ambos (10 px escalado y redondeado). */}
      <span className="relative flex items-center gap-3">
        <GoogleG className="h-6 w-6 shrink-0" />
        <span>{label}</span>
      </span>
    </button>
  );
}
